import { AxiosError } from 'axios';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-semantic-toasts';
import { Button, Form, Segment } from 'semantic-ui/semantic-ui-imports';
import './index.scss';
import LanguageFormSelect from '../LanguageSelector/LanguageFormSelect';
import CountrySelector from '../CountrySelector';
import axios, { refreshCsrfToken } from '../../services/request';

type RegisterErrors = Record<
  | 'first_name'
  | 'last_name'
  | 'address'
  | 'postal_code'
  | 'city'
  | 'country_code'
  | 'tel'
  | 'company'
  | 'vat_no'
  | 'email'
  | 'password1'
  | 'password2'
  | 'preferred_language',
  Array<string>
>;

function Register() {
  // translation
  const { t } = useTranslation();
  const navigate = useNavigate();

  // state
  const [firstName, setFirstName] = React.useState<string | undefined>();
  const [lastName, setLastName] = React.useState<string | undefined>();
  const [address, setAddress] = React.useState<string | undefined>();
  const [postalCode, setPostalCode] = React.useState<string | undefined>();
  const [city, setCity] = React.useState<string | undefined>();
  const [countryCode, setCountryCode] = React.useState<string | undefined>();
  const [languageCode, setLanguageCode] = React.useState<string | undefined>();
  const [tel, setTel] = React.useState<string | undefined>();
  const [company, setCompany] = React.useState<string | undefined>();
  const [vatNo, setVatNo] = React.useState<string | undefined>();
  const [email, setEmail] = React.useState<string | undefined>();
  const [password1, setPassword1] = React.useState<string | undefined>();
  const [password2, setPassword2] = React.useState<string | undefined>();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [errors, setErrors] = React.useState<RegisterErrors>();

  React.useEffect(() => {
    setErrors(undefined);
  }, [
    firstName,
    lastName,
    address,
    postalCode,
    city,
    countryCode,
    tel,
    company,
    vatNo,
    email,
    password1,
    password2,
    languageCode,
  ]);

  const onSubmit = async () => {
    setIsLoading(true);

    await refreshCsrfToken();

    const params = {
      first_name: firstName,
      last_name: lastName,
      address,
      postal_code: postalCode,
      city,
      country_code: countryCode,
      tel,
      company,
      vat_no: vatNo,
      email,
      password1,
      password2,
      preferred_language: languageCode,
    };
    try {
      const { status } = await axios.post(`/rest-auth/registration/`, params);
      if (status === 201) {
        navigate('/verification-email-sent');
      } else {
        toast({
          type: 'error',
          title: t('common.somethingWentWrong'),
          time: 4000,
          size: 'small',
        });
      }
    } catch (error) {
      const err: AxiosError = error as AxiosError;
      if (err.response && typeof err.response?.data === 'object') {
        setErrors(err.response?.data);
      } else {
        toast({
          type: 'error',
          title: t('common.somethingWentWrong'),
          time: 4000,
          size: 'small',
        });
      }
    }
    setIsLoading(false);
  };

  return (
    <div className="register">
      <Form className="register__form" onSubmit={onSubmit}>
        <Segment>
          <Form.Field>
            <Form.Input
              name="firstName"
              label={t('registerPage.firstName')}
              fluid
              icon="user"
              iconPosition="left"
              placeholder={t('registerPage.firstName')}
              onChange={(e: any, { value }: any) => setFirstName(value)}
              error={errors?.first_name}
            />
            <Form.Input
              name="lastName"
              label={t('registerPage.lastName')}
              fluid
              icon="user"
              iconPosition="left"
              placeholder={t('registerPage.lastName')}
              onChange={(e: any, { value }: any) => setLastName(value)}
              error={errors?.last_name}
            />
            <Form.Input
              name="address"
              label={t('registerPage.address')}
              fluid
              icon="address card"
              iconPosition="left"
              placeholder={t('registerPage.address')}
              onChange={(e: any, { value }: any) => setAddress(value)}
              error={errors?.address}
            />
            <Form.Input
              name="postalCode"
              label={t('registerPage.postalCode')}
              fluid
              icon="question"
              iconPosition="left"
              placeholder={t('registerPage.postalCode')}
              onChange={(e: any, { value }: any) => setPostalCode(value)}
              error={errors?.postal_code}
            />
            <Form.Input
              name="city"
              label={t('registerPage.city')}
              fluid
              icon="question"
              iconPosition="left"
              placeholder={t('registerPage.city')}
              onChange={(e: any, { value }: any) => setCity(value)}
              error={errors?.city}
            />
            <CountrySelector
              selected={countryCode || ''}
              placeholder={t('registerPage.countryCode')}
              onSelect={(code: string) => setCountryCode(code)}
              isSearchable={true}
              label={t('registerPage.countryCode')}
              error={errors?.country_code}
            />
            <LanguageFormSelect
              onSelect={(code: string) => setLanguageCode(code)}
              label={t('registerPage.languageCode')}
              placeholder={t('registerPage.languageCode')}
              selected={languageCode || ''}
              error={errors?.preferred_language}
            />
            <Form.Input
              name="tel"
              label={t('registerPage.tel')}
              fluid
              icon="phone"
              iconPosition="left"
              placeholder={t('registerPage.tel')}
              onChange={(e: any, { value }: any) => setTel(value)}
              error={errors?.tel}
            />
            <Form.Input
              name="company"
              label={t('registerPage.company')}
              fluid
              icon="building"
              iconPosition="left"
              placeholder={t('registerPage.company')}
              onChange={(e: any, { value }: any) => setCompany(value)}
              error={errors?.company}
            />
            <Form.Input
              name="vatNo"
              label={t('registerPage.vatNo')}
              fluid
              icon="info"
              iconPosition="left"
              placeholder={t('registerPage.vatNo')}
              onChange={(e: any, { value }: any) => setVatNo(value)}
              error={errors?.vat_no}
            />
            <Form.Input
              name="email"
              label={t('registerPage.email')}
              fluid
              icon="mail"
              iconPosition="left"
              placeholder={t('registerPage.email')}
              onChange={(e: any, { value }: any) => setEmail(value)}
              error={errors?.email}
            />
            <Form.Input
              name="password1"
              label={t('registerPage.password1')}
              fluid
              icon="lock"
              iconPosition="left"
              placeholder={t('registerPage.password1')}
              type="password"
              onChange={(e: any, { value }: any) => setPassword1(value)}
              error={errors?.password1}
            />
            <Form.Input
              name="password2"
              label={t('registerPage.password2')}
              fluid
              icon="lock"
              iconPosition="left"
              placeholder={t('registerPage.password2')}
              type="password"
              onChange={(e: any, { value }: any) => setPassword2(value)}
              error={errors?.password2}
            />
          </Form.Field>
          <Button
            disabled={isLoading}
            loading={isLoading}
            color="teal"
            fluid
            size="large"
            type="submit"
          >
            {t('registerPage.submit')}
          </Button>
          <div>
            <Link to="/login">{t('resetPasswordPage.loginLink')}</Link>
          </div>
        </Segment>
      </Form>
    </div>
  );
}

export default Register;
